import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import QuoteStatusIndicator from '../../../components/ui/QuoteStatusIndicator';

const QuoteTable = ({ quotes, onViewQuote, onResendEmail, onDuplicateQuote, onDeleteQuote }) => {
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-muted transition-smooth"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortField === field && (
          <Icon 
            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
            size={14} 
          />
        )}
      </div>
    </th>
  );

  if (quotes?.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-lg p-12 text-center">
        <Icon name="FileText" size={48} className="mx-auto text-text-secondary mb-4" />
        <h3 className="text-lg font-medium text-text-primary mb-2">Aucun devis trouvé</h3>
        <p className="text-text-secondary mb-6">
          Commencez par créer votre premier devis ou ajustez vos filtres de recherche.
        </p>
        <Button variant="default" iconName="Plus" iconPosition="left">
          Nouveau devis
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              <SortableHeader field="quoteNumber">N° Devis</SortableHeader>
              <SortableHeader field="client">Client</SortableHeader>
              <SortableHeader field="date">Date création</SortableHeader>
              <SortableHeader field="amount">Montant</SortableHeader>
              <SortableHeader field="status">Statut</SortableHeader>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Dernière activité
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border">
            {quotes?.map((quote) => (
              <tr key={quote?.id} className="hover:bg-muted/50 transition-smooth">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-text-primary">
                    {quote?.quoteNumber}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mr-3">
                      <span className="text-xs font-medium text-primary-foreground">
                        {quote?.client?.name?.charAt(0)}{quote?.client?.surname?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-text-primary">
                        {quote?.client?.name} {quote?.client?.surname}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {quote?.client?.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                  {formatDate(quote?.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">
                  {formatAmount(quote?.totalAmount)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <QuoteStatusIndicator status={quote?.status} size="sm" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                  {quote?.lastActivity}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onViewQuote(quote)}
                      title="Voir le devis"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Mail"
                      onClick={() => onResendEmail(quote)}
                      title="Renvoyer par email"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Copy"
                      onClick={() => onDuplicateQuote(quote)}
                      title="Dupliquer"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onDeleteQuote(quote)}
                      className="text-error hover:text-error"
                      title="Supprimer"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {quotes?.map((quote) => (
          <div key={quote?.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-foreground">
                    {quote?.client?.name?.charAt(0)}{quote?.client?.surname?.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-medium text-text-primary">
                    {quote?.client?.name} {quote?.client?.surname}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {quote?.quoteNumber}
                  </div>
                </div>
              </div>
              <QuoteStatusIndicator status={quote?.status} size="sm" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
              <div>
                <span className="text-text-secondary">Date:</span>
                <div className="font-medium text-text-primary">{formatDate(quote?.createdAt)}</div>
              </div>
              <div>
                <span className="text-text-secondary">Montant:</span>
                <div className="font-medium text-text-primary">{formatAmount(quote?.totalAmount)}</div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-secondary">{quote?.lastActivity}</span>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Eye"
                  onClick={() => onViewQuote(quote)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Mail"
                  onClick={() => onResendEmail(quote)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Copy"
                  onClick={() => onDuplicateQuote(quote)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => onDeleteQuote(quote)}
                  className="text-error hover:text-error"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuoteTable;